import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  IconButton,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Divider,
  alpha,
  Tooltip,
  useTheme,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  LocalShipping as ShipIcon,
  Inventory as ReceiveIcon,
  PostAdd as PostIcon,
  Undo as UndoIcon,
  SwapHoriz as TransferIcon,
  AccountBalance as AccountBalanceIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  HourglassTop as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import WarehouseTransferDialog from '../components/warehouseTransfers/WarehouseTransferDialog';
import WarehouseTransferPreviewDialog from '../components/warehouseTransfers/WarehouseTransferPreviewDialog';
import { WarehouseTransfer } from '../types/warehouseTransfers.types';
import { warehouseTransfersService } from '../services/warehouseTransfers.service';

const translations = {
  en: {
    title: 'Warehouse Transfers',
    addWarehouseTransfer: 'New Transfer',
    transferNumber: 'Transfer Number',
    transferDate: 'Transfer Date',
    fromWarehouse: 'From Warehouse',
    toWarehouse: 'To Warehouse',
    itemsCount: 'Items',
    status: 'Status',
    actions: 'Actions',
    search: 'Search by transfer number...',
    noData: 'No warehouse transfers found',
    confirmDelete: 'Are you sure you want to delete this warehouse transfer?',
    confirmShip: 'Ship this transfer? Stock will be deducted from source warehouse.',
    confirmReceive: 'Receive this transfer? Stock will be added to destination warehouse.',
    confirmPost: 'Post this transfer? This will create journal entries.',
    confirmUnpost: 'Unpost this transfer? This will reverse journal entries.',
    confirmApprove: 'Approve this transfer? Journal entry will be created.',
    confirmCancelShip: 'Cancel shipment? Stock will be returned to source warehouse.',
    successDelete: 'Transfer deleted successfully',
    successApprove: 'Transfer approved successfully',
    successShip: 'Transfer shipped successfully',
    successCancelShip: 'Shipment cancelled successfully',
    successReceive: 'Transfer received successfully',
    successPost: 'Transfer posted successfully',
    successUnpost: 'Transfer unposted successfully',
    successReject: 'Transfer rejected successfully',
    errorLoadDetails: 'Failed to load transfer details',
    all: 'All',
    pending: 'Pending',
    draft: 'Draft',
    inTransit: 'In Transit',
    received: 'Received',
    posted: 'Posted',
    rejected: 'Rejected',
    ship: 'Ship',
    cancelShip: 'Cancel Ship',
    receive: 'Receive',
    post: 'Post',
    unpost: 'Unpost',
    approve: 'Approve',
    reject: 'Reject',
    editTransfer: 'Edit',
    deleteTransfer: 'Delete',
    viewTransfer: 'View',
    journalEntry: 'J.E.',
    shippedDate: 'Shipped',
    receivedDate: 'Received',
    fromDate: 'From Date',
    toDate: 'To Date',
    rejectDialogTitle: 'Reject Warehouse Transfer',
    rejectReason: 'Rejection Reason (optional)',
    rejectConfirm: 'Confirm Reject',
    awaitingKeeper: 'Awaiting Keeper',
    totalTransfers: 'Total',
  },
  ar: {
    title: 'تحويلات المخازن',
    addWarehouseTransfer: 'تحويل جديد',
    transferNumber: 'رقم التحويل',
    transferDate: 'تاريخ التحويل',
    fromWarehouse: 'من المخزن',
    toWarehouse: 'إلى المخزن',
    itemsCount: 'الأصناف',
    status: 'الحالة',
    actions: 'الإجراءات',
    search: 'البحث برقم التحويل...',
    noData: 'لا توجد تحويلات مخازن',
    confirmDelete: 'هل أنت متأكد من حذف التحويل؟',
    confirmShip: 'شحن التحويل؟ سيتم خصم المخزون من المصدر.',
    confirmReceive: 'استلام التحويل؟ سيتم إضافة المخزون للوجهة.',
    confirmPost: 'ترحيل التحويل؟ سيتم إنشاء قيد محاسبي.',
    confirmUnpost: 'فك الترحيل؟ سيتم عكس القيد المحاسبي.',
    confirmApprove: 'الموافقة على التحويل؟ سيتم إنشاء القيد المحاسبي.',
    confirmCancelShip: 'إلغاء الشحن؟ سيعود المخزون للمخزن المصدر.',
    successDelete: 'تم حذف التحويل بنجاح',
    successApprove: 'تمت الموافقة على التحويل بنجاح',
    successShip: 'تم شحن التحويل بنجاح',
    successCancelShip: 'تم إلغاء الشحن بنجاح',
    successReceive: 'تم استلام التحويل بنجاح',
    successPost: 'تم ترحيل التحويل بنجاح',
    successUnpost: 'تم فك ترحيل التحويل بنجاح',
    successReject: 'تم رفض التحويل بنجاح',
    errorLoadDetails: 'فشل تحميل تفاصيل التحويل',
    all: 'الكل',
    pending: 'معلّق',
    draft: 'مسودة',
    inTransit: 'قيد الشحن',
    received: 'مستلم',
    posted: 'مرحّل',
    rejected: 'مرفوض',
    ship: 'شحن',
    cancelShip: 'إلغاء الشحن',
    receive: 'استلام',
    post: 'ترحيل',
    unpost: 'فك الترحيل',
    approve: 'موافقة',
    reject: 'رفض',
    editTransfer: 'تعديل',
    deleteTransfer: 'حذف',
    viewTransfer: 'عرض',
    journalEntry: 'ق.م.',
    shippedDate: 'تاريخ الشحن',
    receivedDate: 'تاريخ الاستلام',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    rejectDialogTitle: 'رفض تحويل المخزن',
    rejectReason: 'سبب الرفض (اختياري)',
    rejectConfirm: 'تأكيد الرفض',
    awaitingKeeper: 'بانتظار أمين المخزن',
    totalTransfers: 'الإجمالي',
  },
};

const WarehouseTransfersPage: React.FC = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  // ✅ استخراج صلاحيات المستخدم من التوكن
  const { userBranchIds, userWarehouseIds, isSuperAdmin } = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return { userBranchIds: [] as number[], userWarehouseIds: [] as number[], isSuperAdmin: false };
      const payload = JSON.parse(atob(token.split('.')[1]));
      const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
      const allowedBranches = payload.AllowedBranchId
        ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
        : [];
      const branchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
      const keeperClaims = payload.KeeperWarehouseId;
      const warehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
      const superAdmin = branchIds.length === 0 && warehouseIds.length === 0;
      return { userBranchIds: branchIds, userWarehouseIds: warehouseIds, isSuperAdmin: superAdmin };
    } catch {
      return { userBranchIds: [] as number[], userWarehouseIds: [] as number[], isSuperAdmin: false };
    }
  })();

  const isWarehouseKeeper = userWarehouseIds.length > 0;

  const canSeeTransfer = (wt: WarehouseTransfer) => {
    if (isSuperAdmin) return true;
    if (wt.fromWarehouseId && userWarehouseIds.includes(wt.fromWarehouseId)) return true;
    if (wt.toWarehouseId && userWarehouseIds.includes(wt.toWarehouseId)) return true;
    return false;
  };

  // ✅ أمين المخزن المصدر أو السوبر أدمن يوافق/يرفض
  const canApproveReject = (wt: WarehouseTransfer) => {
    if (isSuperAdmin) return true;
    if (isWarehouseKeeper && wt.fromWarehouseId && userWarehouseIds.includes(wt.fromWarehouseId)) return true;
    return false;
  };

  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();

  const [warehouseTransfers, setWarehouseTransfers] = useState<WarehouseTransfer[]>([]);
  const [filteredWarehouseTransfers, setFilteredWarehouseTransfers] = useState<WarehouseTransfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedWarehouseTransfer, setSelectedWarehouseTransfer] = useState<WarehouseTransfer | undefined>();
  const [activeTab, setActiveTab] = useState(0);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedForMenu, setSelectedForMenu] = useState<WarehouseTransfer | null>(null);

  // ✅ دايلوج التأكيد
  type ConfirmAction = 'delete' | 'approve' | 'ship' | 'cancelShip' | 'receive' | 'cancelReceive' | 'post' | 'unpost';
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; action: ConfirmAction; transferId: number; transferNumber: string }>({
    open: false, action: 'delete', transferId: 0, transferNumber: '',
  });

  const openConfirm = (action: ConfirmAction, wt: WarehouseTransfer) => {
    handleCloseMenu();
    setConfirmDialog({ open: true, action, transferId: wt.transferId, transferNumber: wt.transferNumber });
  };

  const closeConfirm = () => setConfirmDialog(prev => ({ ...prev, open: false }));

  const handleConfirm = async () => {
    const { action, transferId } = confirmDialog;
    closeConfirm();
    try {
      if (action === 'delete') {
        await warehouseTransfersService.delete(transferId);
        toast.success(t.successDelete);
      } else if (action === 'approve') {
        await warehouseTransfersService.approve(transferId);
        toast.success(t.successApprove);
      } else if (action === 'ship') {
        await warehouseTransfersService.ship(transferId);
        toast.success(t.successShip);
      } else if (action === 'cancelShip') {
        await warehouseTransfersService.cancelShip(transferId);
        toast.success(t.successCancelShip);
      } else if (action === 'receive') {
        await warehouseTransfersService.receive(transferId);
        toast.success(t.successReceive);
      } else if (action === 'cancelReceive') {
        await warehouseTransfersService.cancelReceive(transferId);
        toast.success(isRTL ? 'تم إلغاء الاستلام' : 'Receive cancelled');
      } else if (action === 'post') {
        await warehouseTransfersService.post(transferId);
        toast.success(t.successPost);
      } else if (action === 'unpost') {
        await warehouseTransfersService.unpost(transferId);
        toast.success(t.successUnpost);
      }
      loadWarehouseTransfers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Operation failed');
    }
  };

  const confirmConfig: Record<ConfirmAction, { color: string; gradientFrom: string; gradientTo: string; icon: React.ReactNode; buttonColor: 'error' | 'success' | 'warning' | 'info'; message: string; buttonLabel: string }> = {
    delete:     { color: '#d32f2f', gradientFrom: '#d32f2f', gradientTo: '#ef5350', icon: <DeleteIcon />, buttonColor: 'error',   message: t.confirmDelete,     buttonLabel: isRTL ? 'حذف' : 'Delete' },
    approve:    { color: '#2e7d32', gradientFrom: '#2e7d32', gradientTo: '#43a047', icon: <ApproveIcon />, buttonColor: 'success', message: t.confirmApprove,    buttonLabel: t.approve },
    ship:       { color: '#e65100', gradientFrom: '#e65100', gradientTo: '#ef6c00', icon: <ShipIcon />,    buttonColor: 'warning', message: t.confirmShip,       buttonLabel: t.ship },
    cancelShip: { color: '#c62828', gradientFrom: '#c62828', gradientTo: '#e53935', icon: <UndoIcon />,    buttonColor: 'error',   message: t.confirmCancelShip, buttonLabel: t.cancelShip },
    receive:    { color: '#0277bd', gradientFrom: '#0277bd', gradientTo: '#0288d1', icon: <ReceiveIcon />, buttonColor: 'info',    message: t.confirmReceive,    buttonLabel: t.receive },
    cancelReceive: { color: '#c62828', gradientFrom: '#c62828', gradientTo: '#e53935', icon: <UndoIcon />, buttonColor: 'error', message: isRTL ? 'إلغاء الاستلام؟ سيتم حذف البضاعة من المخزن المستلم.' : 'Cancel receive? Stock will be removed from destination.', buttonLabel: isRTL ? 'إلغاء الاستلام' : 'Cancel Receive' },
    post:       { color: '#1b5e20', gradientFrom: '#1b5e20', gradientTo: '#2e7d32', icon: <PostIcon />,    buttonColor: 'success', message: t.confirmPost,       buttonLabel: t.post },
    unpost:     { color: '#e65100', gradientFrom: '#e65100', gradientTo: '#f57c00', icon: <UndoIcon />,    buttonColor: 'warning', message: t.confirmUnpost,     buttonLabel: t.unpost },
  };

  // ✅ دايلوج الرفض
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [transferToReject, setTransferToReject] = useState<WarehouseTransfer | null>(null);

  useEffect(() => {
    loadWarehouseTransfers();
  }, []);

  useEffect(() => {
    filterWarehouseTransfers();
  }, [searchTerm, warehouseTransfers, activeTab, fromDate, toDate]);

  const loadWarehouseTransfers = async () => {
    setLoading(true);
    try {
      const data = await warehouseTransfersService.getAll();
      const filtered = data.filter((wt: WarehouseTransfer) => canSeeTransfer(wt));
      setWarehouseTransfers(filtered);
    } catch (error) {
      console.error('Failed to load warehouse transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterWarehouseTransfers = () => {
    let filtered = warehouseTransfers;

    // تاب
    if (activeTab === 1) filtered = filtered.filter(wt => wt.status === 'Pending');
    else if (activeTab === 2) filtered = filtered.filter(wt => wt.status === 'Draft');
    else if (activeTab === 3) filtered = filtered.filter(wt => wt.status === 'InTransit');
    else if (activeTab === 4) filtered = filtered.filter(wt => wt.status === 'Received');
    else if (activeTab === 5) filtered = filtered.filter(wt => wt.status === 'Posted');
    else if (activeTab === 6) filtered = filtered.filter(wt => wt.status === 'Rejected');

    // فلتر تاريخ
    if (fromDate) filtered = filtered.filter(wt => wt.transferDate.split('T')[0] >= fromDate);
    if (toDate) filtered = filtered.filter(wt => wt.transferDate.split('T')[0] <= toDate);

    // بحث
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(wt =>
        wt.transferNumber.toLowerCase().includes(term) ||
        wt.fromWarehouseName?.toLowerCase().includes(term) ||
        wt.toWarehouseName?.toLowerCase().includes(term)
      );
    }

    // ترتيب حسب رقم التحويل تنازلياً
    filtered.sort((a, b) => b.transferNumber.localeCompare(a.transferNumber, undefined, { numeric: true }));

    setFilteredWarehouseTransfers(filtered);
  };

  const calculateStats = () => {
    const total = warehouseTransfers.length;
    const pendingCount = warehouseTransfers.filter(wt => wt.status === 'Pending').length;
    const draftCount = warehouseTransfers.filter(wt => wt.status === 'Draft').length;
    const inTransitCount = warehouseTransfers.filter(wt => wt.status === 'InTransit').length;
    const receivedCount = warehouseTransfers.filter(wt => wt.status === 'Received').length;
    const postedCount = warehouseTransfers.filter(wt => wt.status === 'Posted').length;
    const rejectedCount = warehouseTransfers.filter(wt => wt.status === 'Rejected').length;
    return { total, pendingCount, draftCount, inTransitCount, receivedCount, postedCount, rejectedCount };
  };

  const stats = calculateStats();

  const handleAdd = () => {
    setSelectedWarehouseTransfer(undefined);
    setDialogOpen(true);
  };

  const handleView = async (wt: WarehouseTransfer) => {
    handleCloseMenu();
    try {
      const fullTransfer = await warehouseTransfersService.getById(wt.transferId);
      setSelectedWarehouseTransfer(fullTransfer);
      setPreviewDialogOpen(true);
    } catch (error) {
      console.error('Failed to load transfer details:', error);
      toast.error(t.errorLoadDetails);
    }
  };

  const handleEdit = async (wt: WarehouseTransfer) => {
    handleCloseMenu();
    try {
      const fullTransfer = await warehouseTransfersService.getById(wt.transferId);
      setSelectedWarehouseTransfer(fullTransfer);
      setDialogOpen(true);
    } catch (error) {
      console.error('Failed to load transfer details:', error);
      toast.error(t.errorLoadDetails);
    }
  };

  const handleEditFromPreview = () => {
    setPreviewDialogOpen(false);
    setDialogOpen(true);
  };

  const handleDelete = (wt: WarehouseTransfer) => openConfirm('delete', wt);

  // ✅ موافقة
  const handleApprove = (wt: WarehouseTransfer) => openConfirm('approve', wt);

  // ✅ فتح دايلوج الرفض
  const handleOpenRejectDialog = (wt: WarehouseTransfer) => {
    setTransferToReject(wt);
    setRejectReason('');
    setRejectDialogOpen(true);
    handleCloseMenu();
  };

  // ✅ تأكيد الرفض
  const handleConfirmReject = async () => {
    if (!transferToReject) return;
    try {
      await warehouseTransfersService.reject(transferToReject.transferId, rejectReason || undefined);
      setRejectDialogOpen(false);
      setTransferToReject(null);
      setRejectReason('');
      toast.success(t.successReject);
      loadWarehouseTransfers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject');
    }
  };

  const handleShip = (wt: WarehouseTransfer) => openConfirm('ship', wt);

  // ✅ إلغاء الشحن — InTransit → Draft
  const handleCancelShip = (wt: WarehouseTransfer) => openConfirm('cancelShip', wt);
  const handleCancelReceive = (wt: WarehouseTransfer) => openConfirm('cancelReceive', wt);

  const handleReceive = (wt: WarehouseTransfer) => openConfirm('receive', wt);

  const handlePost = (wt: WarehouseTransfer) => openConfirm('post', wt);

  const handleUnpost = (wt: WarehouseTransfer) => openConfirm('unpost', wt);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, wt: WarehouseTransfer) => {
    setAnchorEl(event.currentTarget);
    setSelectedForMenu(wt);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedForMenu(null);
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'Pending': return { color: 'warning' as const, icon: <PendingIcon />, borderColor: 'warning.main', label: t.pending };
      case 'Draft': return { color: 'info' as const, icon: <CheckCircleIcon />, borderColor: 'info.main', label: t.draft };
      case 'InTransit': return { color: 'secondary' as const, icon: <ShipIcon />, borderColor: 'secondary.main', label: t.inTransit };
      case 'Received': return { color: 'primary' as const, icon: <ReceiveIcon />, borderColor: 'primary.main', label: t.received };
      case 'Posted': return { color: 'success' as const, icon: <CheckCircleIcon />, borderColor: 'success.main', label: t.posted };
      case 'Rejected': return { color: 'error' as const, icon: <BlockIcon />, borderColor: 'error.main', label: t.rejected };
      default: return { color: 'default' as const, icon: <ScheduleIcon />, borderColor: 'grey.400', label: status };
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 56, height: 56, background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)' }}>
            <TransferIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{t.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.total} {isRTL ? 'تحويل' : 'transfers'}
              {stats.pendingCount > 0 && (
                <Chip label={`${stats.pendingCount} ${t.pending}`} size="small" color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
              )}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadWarehouseTransfers} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} size="large"
            sx={{ background: 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)', boxShadow: '0 3px 5px 2px rgba(156, 39, 176, .3)', px: 4, py: 1.5 }}>
            {t.addWarehouseTransfer}
          </Button>
        </Box>
      </Stack>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card sx={{ background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalTransfers}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.total}</Typography>
              </Box>
              <TransferIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #ff9a56 0%, #ff6a00 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.pending}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.pendingCount}</Typography>
              </Box>
              <PendingIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" spacing={2}>
              <Box><Typography variant="h6" sx={{ fontWeight: 700 }}>{stats.draftCount}</Typography><Typography variant="caption">{t.draft}</Typography></Box>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
              <Box><Typography variant="h6" sx={{ fontWeight: 700 }}>{stats.inTransitCount}</Typography><Typography variant="caption">{t.inTransit}</Typography></Box>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
              <Box><Typography variant="h6" sx={{ fontWeight: 700 }}>{stats.receivedCount}</Typography><Typography variant="caption">{t.received}</Typography></Box>
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" spacing={2}>
              <Box><Typography variant="h6" sx={{ fontWeight: 700 }}>{stats.postedCount}</Typography><Typography variant="caption">{t.posted}</Typography></Box>
              {stats.rejectedCount > 0 && (
                <><Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                <Box><Typography variant="h6" sx={{ fontWeight: 700, color: '#fee2e2' }}>{stats.rejectedCount}</Typography><Typography variant="caption">{t.rejected}</Typography></Box></>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Search + Filter + Tabs */}
      <Paper elevation={3} sx={{ mb: 3, background: theme.palette.mode === 'dark' ? 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)' : 'linear-gradient(135deg, #e0e7ff 0%, #cffafe 100%)', borderRadius: 3 }}>
        <Stack spacing={2} sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField fullWidth placeholder={t.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="primary" /></InputAdornment> }} />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.fromDate}
                value={fromDate ? new Date(fromDate) : null}
                onChange={(date) => {
                  const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setFromDate(newVal);
                  if (toDate && newVal && toDate < newVal) setToDate('');
                }}
                format="dd/MM/yyyy"
                minDate={minDate}
                maxDate={toDate ? new Date(toDate) : maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { backgroundColor: 'background.paper', borderRadius: 2, minWidth: 170 } } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.toDate}
                value={toDate ? new Date(toDate) : null}
                onChange={(date) => {
                  const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setToDate(newVal);
                  if (fromDate && newVal && fromDate > newVal) setFromDate('');
                }}
                format="dd/MM/yyyy"
                minDate={fromDate ? new Date(fromDate) : minDate}
                maxDate={maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { backgroundColor: 'background.paper', borderRadius: 2, minWidth: 170 } } }}
              />
            </LocalizationProvider>
          </Stack>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label={<Badge badgeContent={stats.total} color="primary"><span>{t.all}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.pendingCount} color="warning"><span>{t.pending}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.draftCount} color="info"><span>{t.draft}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.inTransitCount} color="secondary"><span>{t.inTransit}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.receivedCount} color="primary"><span>{t.received}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.postedCount} color="success"><span>{t.posted}</span></Badge>} />
            {stats.rejectedCount > 0 && (
              <Tab label={<Badge badgeContent={stats.rejectedCount} color="error"><span>{t.rejected}</span></Badge>} />
            )}
          </Tabs>
        </Stack>
      </Paper>

      {/* Transfers List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={60} /></Box>
      ) : (
        <Stack spacing={2}>
          {filteredWarehouseTransfers.map((wt, idx) => {
            const statusConfig = getStatusConfig(wt.status || 'Pending');
            return (
              <Fade in timeout={300 + idx * 100} key={wt.transferId}>
                <Card elevation={3} sx={{
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 },
                  border: 2, borderColor: statusConfig.borderColor,
                  borderLeft: 6, borderLeftColor: statusConfig.borderColor,
                  ...(wt.status === 'Pending' && {
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.4)}` },
                      '70%': { boxShadow: `0 0 0 10px ${alpha(theme.palette.warning.main, 0)}` },
                      '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0)}` },
                    },
                  }),
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ width: 50, height: 50, bgcolor: `${statusConfig.color}.main` }}><TransferIcon /></Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{wt.transferNumber}</Typography>
                          <Typography variant="body2" component="div" color="text.secondary">{formatDate(wt.transferDate)}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={statusConfig.label} color={statusConfig.color} icon={statusConfig.icon} sx={{ fontWeight: 600, minWidth: 100 }} />
                        {wt.status === 'Pending' && (
                          <Tooltip title={t.awaitingKeeper}>
                            <Chip label={t.awaitingKeeper} size="small" color="warning" variant="outlined" icon={<PendingIcon />} />
                          </Tooltip>
                        )}
                        {wt.journalEntryId && (
                          <Chip label={`${t.journalEntry} ${wt.journalEntryNumber || 'JE'} (#${wt.journalEntryId})`} size="small" color="info" variant="outlined" />
                        )}
                        {/* أزرار سريعة للموافقة/الرفض */}
                        {(wt.status === 'Pending' || wt.status === 'Rejected') && canApproveReject(wt) && (
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title={t.approve}>
                              <IconButton size="small" onClick={() => handleApprove(wt)}
                                sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.25) } }}>
                                <ApproveIcon fontSize="small" color="success" />
                              </IconButton>
                            </Tooltip>
                            {wt.status === 'Pending' && (
                              <Tooltip title={t.reject}>
                                <IconButton size="small" onClick={() => handleOpenRejectDialog(wt)}
                                  sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.25) } }}>
                                  <RejectIcon fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                        <IconButton onClick={(e) => handleOpenMenu(e, wt)}><MoreVertIcon /></IconButton>
                      </Stack>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t.fromWarehouse}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>{isRTL ? wt.fromWarehouseNameAr : wt.fromWarehouseName}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t.toWarehouse}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>{isRTL ? wt.toWarehouseNameAr : wt.toWarehouseName}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t.itemsCount}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{wt.details.length}</Typography>
                      </Box>
                      {(wt.shippedDate || wt.receivedDate) && (
                        <Box>
                          {wt.shippedDate && <><Typography variant="caption" color="text.secondary">{t.shippedDate}</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(wt.shippedDate)}</Typography></>}
                          {wt.receivedDate && <><Typography variant="caption" color="text.secondary">{t.receivedDate}</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(wt.receivedDate)}</Typography></>}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            );
          })}
          {filteredWarehouseTransfers.length === 0 && (
            <Paper sx={{ p: 8, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <TransferIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">{t.noData}</Typography>
            </Paper>
          )}
        </Stack>
      )}

      {/* ✅ قائمة الإجراءات */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu} PaperProps={{ elevation: 3, sx: { minWidth: 220, mt: 1 } }}>
        <MenuItem onClick={() => selectedForMenu && handleView(selectedForMenu)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} color="primary" /><Typography color="primary.main">{t.viewTransfer}</Typography>
        </MenuItem>
        <Divider />

        {/* Pending/Rejected: موافقة */}
        {(selectedForMenu?.status === 'Pending' || selectedForMenu?.status === 'Rejected') && canApproveReject(selectedForMenu) && (
          <MenuItem onClick={() => handleApprove(selectedForMenu)}>
            <ApproveIcon fontSize="small" sx={{ mr: 1 }} color="success" /><Typography color="success.main" fontWeight={600}>{t.approve}</Typography>
          </MenuItem>
        )}
        {/* Pending: رفض */}
        {selectedForMenu?.status === 'Pending' && canApproveReject(selectedForMenu) && (
          <MenuItem onClick={() => handleOpenRejectDialog(selectedForMenu)}>
            <RejectIcon fontSize="small" sx={{ mr: 1 }} color="error" /><Typography color="error.main" fontWeight={600}>{t.reject}</Typography>
          </MenuItem>
        )}
        {(selectedForMenu?.status === 'Pending' || selectedForMenu?.status === 'Rejected') && canApproveReject(selectedForMenu!) && <Divider />}

        {/* Pending: تعديل/حذف لأي شخص له صلاحية */}
        {selectedForMenu?.status === 'Pending' && canSeeTransfer(selectedForMenu) && (
          <MenuItem onClick={() => handleEdit(selectedForMenu)}><EditIcon fontSize="small" sx={{ mr: 1 }} />{t.editTransfer}</MenuItem>
        )}
        {selectedForMenu?.status === 'Pending' && canSeeTransfer(selectedForMenu) && (
          <MenuItem onClick={() => handleDelete(selectedForMenu)}><DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" /><Typography color="error">{t.deleteTransfer}</Typography></MenuItem>
        )}

        {/* Draft: تعديل/حذف فقط سوبر أدمن */}
        {selectedForMenu?.status === 'Draft' && isSuperAdmin && (
          <MenuItem onClick={() => handleEdit(selectedForMenu)}><EditIcon fontSize="small" sx={{ mr: 1 }} />{t.editTransfer}</MenuItem>
        )}
        {selectedForMenu?.status === 'Draft' && isSuperAdmin && (
          <MenuItem onClick={() => handleDelete(selectedForMenu)}><DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" /><Typography color="error">{t.deleteTransfer}</Typography></MenuItem>
        )}

        {/* Draft: شحن */}
        {selectedForMenu?.status === 'Draft' && (isSuperAdmin || (isWarehouseKeeper && userWarehouseIds.includes(selectedForMenu.fromWarehouseId))) && (
          <MenuItem onClick={() => handleShip(selectedForMenu)}>
            <ShipIcon fontSize="small" sx={{ mr: 1 }} color="warning" /><Typography color="warning.main">{t.ship}</Typography>
          </MenuItem>
        )}

        {/* InTransit: إلغاء شحن — أمين مخزن المصدر أو سوبر أدمن */}
        {selectedForMenu?.status === 'InTransit' && (isSuperAdmin || (isWarehouseKeeper && userWarehouseIds.includes(selectedForMenu.fromWarehouseId))) && (
          <MenuItem onClick={() => handleCancelShip(selectedForMenu)}>
            <UndoIcon fontSize="small" sx={{ mr: 1 }} color="error" /><Typography color="error.main">{t.cancelShip}</Typography>
          </MenuItem>
        )}

        {/* InTransit: استلام — أمين مخزن الوجهة أو سوبر أدمن */}
        {selectedForMenu?.status === 'InTransit' && (isSuperAdmin || (isWarehouseKeeper && userWarehouseIds.includes(selectedForMenu.toWarehouseId))) && (
          <MenuItem onClick={() => handleReceive(selectedForMenu)}>
            <ReceiveIcon fontSize="small" sx={{ mr: 1 }} color="info" /><Typography color="info.main">{t.receive}</Typography>
          </MenuItem>
        )}

        {/* Received: ترحيل — سوبر أدمن */}
        {selectedForMenu?.status === 'Received' && canPost && (
          <MenuItem onClick={() => handlePost(selectedForMenu)}>
            <PostIcon fontSize="small" sx={{ mr: 1 }} color="success" /><Typography color="success.main">{t.post}</Typography>
          </MenuItem>
        )}

        {/* Received: إلغاء الاستلام — سوبر أدمن فقط */}
        {selectedForMenu?.status === 'Received' && isSuperAdmin && (
          <MenuItem onClick={() => handleCancelReceive(selectedForMenu)}>
            <UndoIcon fontSize="small" sx={{ mr: 1 }} color="error" /><Typography color="error.main">{isRTL ? 'إلغاء الاستلام' : 'Cancel Receive'}</Typography>
          </MenuItem>
        )}

        {/* Posted: فك ترحيل — سوبر أدمن */}
        {selectedForMenu?.status === 'Posted' && canPost && (
          <MenuItem onClick={() => handleUnpost(selectedForMenu)}>
            <UndoIcon fontSize="small" sx={{ mr: 1 }} color="warning" /><Typography color="warning.main">{t.unpost}</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* ✅ دايلوج التأكيد */}
      {confirmDialog.open && (() => {
        const cfg = confirmConfig[confirmDialog.action];
        return (
          <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
            <DialogTitle sx={{
              background: `linear-gradient(135deg, ${cfg.gradientFrom} 0%, ${cfg.gradientTo} 100%)`,
              color: 'white', display: 'flex', alignItems: 'center', gap: 1,
            }}>
              {cfg.icon}
              <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                {confirmDialog.transferNumber}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, mt: 1 }}>
              <Typography>{cfg.message}</Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={closeConfirm} variant="outlined" sx={{ flex: 1 }}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={handleConfirm} variant="contained" color={cfg.buttonColor} sx={{ flex: 1 }}>
                {cfg.buttonLabel}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* ✅ دايلوج الرفض */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <RejectIcon />{t.rejectDialogTitle}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          {transferToReject && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip label={transferToReject.transferNumber} color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? transferToReject.fromWarehouseNameAr : transferToReject.fromWarehouseName} → {isRTL ? transferToReject.toWarehouseNameAr : transferToReject.toWarehouseName}
                </Typography>
              </Stack>
              <TextField fullWidth multiline rows={3} label={t.rejectReason} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder={isRTL ? 'مثال: الكمية غير متوفرة حالياً' : 'e.g. Stock not available'} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} variant="outlined">{isRTL ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={handleConfirmReject} variant="contained" color="error" startIcon={<RejectIcon />}>{t.rejectConfirm}</Button>
        </DialogActions>
      </Dialog>

      <WarehouseTransferDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setSelectedWarehouseTransfer(undefined); }} warehouseTransfer={selectedWarehouseTransfer} onSave={loadWarehouseTransfers} />
      <WarehouseTransferPreviewDialog open={previewDialogOpen} onClose={() => { setPreviewDialogOpen(false); setSelectedWarehouseTransfer(undefined); }} transfer={selectedWarehouseTransfer || null} onEdit={handleEditFromPreview} />
    </Box>
  );
};

export default WarehouseTransfersPage;